// (c) chaehongee. Confidential and proprietary.

import { useCallback, useRef, useState } from 'react'
import type { Dispatch } from '@reduxjs/toolkit'
import type { ToolMode } from '../toolbar/useToolMode'
import type { Rect } from '../../types'
import { rectsIntersect } from '../../utils/geometry'
import { setNodes } from '../../../../store/slices/selectionSlice'

export interface MarqueeRect {
    startX: number
    startY: number
    endX: number
    endY: number
}

interface UseMarqueeSelectionParams {
    containerRef: React.RefObject<HTMLDivElement | null>
    clientToWorld: (clientX: number, clientY: number) => { x: number; y: number } | null
    worldRectsById: Record<string, Rect>
    toolMode: ToolMode
    sessionId: string | null
    dispatch: Dispatch
    currentSelectedIds: string[]
}

const MIN_DRAG_PX = 3

export function useMarqueeSelection(params: UseMarqueeSelectionParams) {
    const {
        containerRef,
        clientToWorld,
        worldRectsById,
        toolMode,
        sessionId,
        dispatch,
        currentSelectedIds,
    } = params

    const [marqueeRect, setMarqueeRect] = useState<MarqueeRect | null>(null)
    const activeRef = useRef(false)
    const startRef = useRef<{ x: number; y: number } | null>(null)

    const startMarquee = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const el = containerRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        startRef.current = { x, y }
        activeRef.current = true
        el.setPointerCapture(e.pointerId)
        setMarqueeRect({ startX: x, startY: y, endX: x, endY: y })
    }, [containerRef])

    const moveMarquee = useCallback((e: React.PointerEvent<HTMLDivElement>): boolean => {
        if (!activeRef.current || !startRef.current) return false
        const el = containerRef.current
        if (!el) return false
        const rect = el.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        setMarqueeRect((prev) =>
            prev ? { ...prev, endX: x, endY: y } : null
        )
        return true
    }, [containerRef])

    const endMarquee = useCallback((e: React.PointerEvent<HTMLDivElement>): boolean => {
        if (!activeRef.current || !startRef.current) return false
        activeRef.current = false
        const el = containerRef.current
        if (el) {
            try { el.releasePointerCapture(e.pointerId) } catch { /* noop */ }
        }

        const start = startRef.current
        startRef.current = null
        setMarqueeRect(null)

        if (!start || !sessionId) return true

        const elRect = el?.getBoundingClientRect()
        if (!elRect) return true

        const endX = e.clientX - elRect.left
        const endY = e.clientY - elRect.top

        // Tiny drag → treat as background click (clear selection unless shift held)
        const dx = Math.abs(endX - start.x)
        const dy = Math.abs(endY - start.y)
        if (dx < MIN_DRAG_PX && dy < MIN_DRAG_PX) {
            if (!e.shiftKey) {
                dispatch(setNodes({ sessionId, nodeIds: [] }))
            }
            return true
        }

        // Convert screen rect to world rect
        const worldTopLeft = clientToWorld(
            elRect.left + Math.min(start.x, endX),
            elRect.top + Math.min(start.y, endY)
        )
        const worldBottomRight = clientToWorld(
            elRect.left + Math.max(start.x, endX),
            elRect.top + Math.max(start.y, endY)
        )
        if (!worldTopLeft || !worldBottomRight) return true

        const marqueeWorld: Rect = {
            x: worldTopLeft.x,
            y: worldTopLeft.y,
            w: worldBottomRight.x - worldTopLeft.x,
            h: worldBottomRight.y - worldTopLeft.y,
        }

        // Find items that intersect
        const hitIds: string[] = []
        for (const [id, itemRect] of Object.entries(worldRectsById)) {
            if (rectsIntersect(marqueeWorld, itemRect)) {
                hitIds.push(id)
            }
        }

        if (toolMode === 'select' || e.shiftKey) {
            // Toggle (Figma-style): marqueed items already selected get removed, others get added
            const hitSet = new Set(hitIds)
            const kept = currentSelectedIds.filter((id) => !hitSet.has(id))
            const added = hitIds.filter((id) => !currentSelectedIds.includes(id))
            dispatch(setNodes({ sessionId, nodeIds: [...kept, ...added] }))
        } else {
            // Pointer mode: replace selection
            dispatch(setNodes({ sessionId, nodeIds: hitIds }))
        }

        return true
    }, [clientToWorld, worldRectsById, toolMode, sessionId, dispatch, currentSelectedIds, containerRef])

    return {
        marqueeRect,
        startMarquee,
        moveMarquee,
        endMarquee,
        isMarqueeActive: activeRef.current,
    }
}
